import React from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '../../services/product.service';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/Button';
import { Heart, ShoppingCart, Star } from 'lucide-react';
import { useCartStore } from '../../store/cart.store';
import { useToast } from '../ui/ToastProvider';
import { productService } from '../../services/product.service';

interface ProductCardProps {
  product: Product;
  onWishlistToggle?: (productId: number) => void;
  inWishlist?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onWishlistToggle,
  inWishlist = false,
}) => {
  const { addItem } = useCartStore();
  const { showToast } = useToast();

  const featuredImage = product.images?.find((img) => img.isFeatured) || product.images?.[0];
  const discount = product.comparePrice
    ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
    : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock <= 0) {
      showToast('Sorry, this product is out of stock', 'error');
      return;
    }
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: featuredImage?.url || '/placeholder.png',
    });
    showToast(`${product.name} added to cart`, 'success');
  };

  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWishlistToggle) {
      onWishlistToggle(product.id);
      return;
    }
    try {
      if (inWishlist) {
        await productService.removeFromWishlist(product.id);
        showToast('Removed from wishlist', 'info');
      } else {
        await productService.addToWishlist(product.id);
        showToast(`${product.name} saved to your wishlist`, 'success');
      }
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response?.status;
      showToast(status === 401 ? 'Sign in to save products to your wishlist' : status === 409 ? 'Product is already in your wishlist' : 'Unable to update your wishlist', 'error');
    }
  };

  return (
    <Link to={`/shop/${product.slug}`}>
      <Card className="group h-full overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg">
        <div className="relative aspect-square overflow-hidden bg-[rgb(var(--background))]">
          <img
            src={featuredImage?.url || '/placeholder-product.png'}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {discount > 0 && (
            <span className="absolute top-2 right-2 rounded-full bg-red-500 px-2 py-1 text-xs font-bold text-white">
              -{discount}%
            </span>
          )}
          {product.stock <= 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <span className="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-semibold text-white">
                Out of Stock
              </span>
            </div>
          )}
          <button
            onClick={handleWishlistToggle}
            aria-label={`Add ${product.name} to wishlist`}
            className="absolute left-1 top-1 rounded-full bg-white/85 p-1.5 transition-opacity sm:left-2 sm:top-2 sm:p-2 sm:opacity-0 sm:group-hover:opacity-100 dark:bg-dark-surface/80"
          >
            <Heart
              className={`h-4 w-4 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-[rgb(var(--light-secondary))]'}`}
            />
          </button>
        </div>
        <CardContent className="p-2 sm:p-4">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex items-center justify-between">
              <span className="hidden text-xs font-medium uppercase text-[rgb(var(--light-secondary))] sm:inline">
                {product.categoryId}
              </span>
              {product.averageRating > 0 && (
                <span className="flex items-center text-xs text-yellow-500">
                  <Star className="mr-0.5 h-3 w-3 fill-current" />
                  {product.averageRating.toFixed(1)}
                </span>
              )}
            </div>
            <h3 className="line-clamp-2 text-xs font-semibold text-[rgb(var(--foreground))] sm:text-sm">
              {product.name}
            </h3>
            <p className="hidden line-clamp-2 text-xs text-[rgb(var(--light-secondary))] sm:block">
              {product.shortDescription}
            </p>
            <div className="flex items-center justify-between pt-1">
              <div>
                <span className="text-lg font-bold text-primary">
                  <span className="text-sm sm:text-lg">GHS {(product.price / 100).toFixed(2)}</span>
                </span>
                {product.comparePrice && (
                  <span className="ml-1 hidden text-sm text-[rgb(var(--light-secondary))] line-through sm:inline">
                    GHS {(product.comparePrice / 100).toFixed(2)}
                  </span>
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                aria-label={`Add ${product.name} to cart`}
                className="rounded-full p-2 sm:p-2.5"
              >
                <ShoppingCart className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};